import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Trash2,
  Upload,
  X,
  AlertCircle,
  Image,
  Camera,
  RefreshCw,
  Eye,
  EyeOff,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { getAdminAuthConfig } from "../../lib/adminAuth";

// Couleurs du drapeau du Niger
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

const BackendUrl = process.env.REACT_APP_Backend_Url;

function ProductPub() {
  const [allPub, setAllPub] = useState([]);
  const [image, setImage] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showFullImage, setShowFullImage] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const fetchPublications = () => {
    setIsRefreshing(true);
    axios
      .get(`${BackendUrl}/api/bannersRoutes`)
      .then((response) => {
        setAllPub(response?.data?.length > 0 ? response?.data : []);
      })
      .catch((error) => {
        toast({
          title: "Erreur",
          description: "Impossible de charger les publications",
          variant: "destructive",
        });
        console.log(error);
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validation de l'image
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Format non supporté",
        description:
          "Veuillez sélectionner une image au format JPEG, PNG, WEBP ou GIF",
        variant: "destructive",
      });
      return;
    }

    // Limite de taille: 5MB
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Image trop volumineuse",
        description: "La taille de l'image ne doit pas dépasser 5MB",
        variant: "destructive",
      });
      return;
    }

    setImage(file);
    setPreviewImage(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setImage(null);
    setPreviewImage(null);
    setTitle("");
    setDescription("");
    setIsPublished(false);
    setEditItem(null);
  };

  const creer = (e) => {
    e.preventDefault();
    if (!image && !editItem) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner une image",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    const formData = new FormData();

    if (image) {
      formData.append("image", image);
    }

    formData.append("title", title);
    formData.append("description", description);
    formData.append("pub", isPublished);

    if (editItem) {
      // Mise à jour d'une publication existante
      axios
        .put(`${BackendUrl}/api/bannersRoutes/${editItem._id}`, formData, getAdminAuthConfig())
        .then(() => {
          toast({
            title: "Succès",
            description: "Publication mise à jour avec succès",
            style: {
              backgroundColor: COLORS.paleGreen,
              borderColor: COLORS.green,
            },
          });
          resetForm();
          fetchPublications();
        })
        .catch((error) => {
          console.log(error);
          toast({
            title: "Erreur",
            description: "Échec de la mise à jour de la publication",
            variant: "destructive",
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // Création d'une nouvelle publication
      axios
        .post(`${BackendUrl}/api/bannersRoutes`, formData, getAdminAuthConfig())
        .then(() => {
          toast({
            title: "Succès",
            description: "Publication créée avec succès",
            style: {
              backgroundColor: COLORS.paleGreen,
              borderColor: COLORS.green,
            },
          });
          resetForm();
          fetchPublications();
        })
        .catch((error) => {
          console.log(error);
          toast({
            title: "Erreur",
            description: "Échec de la création de la publication",
            variant: "destructive",
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const deletProductPub = (id) => {
    setIsLoading(true);
    axios
      .delete(`${BackendUrl}/api/bannersRoutes/${id}`, getAdminAuthConfig())
      .then(() => {
        toast({
          title: "Succès",
          description: "Publication supprimée",
          style: {
            backgroundColor: COLORS.paleGreen,
            borderColor: COLORS.green,
          },
        });
        setConfirmDelete(null);
        fetchPublications();
      })
      .catch((error) => {
        toast({
          title: "Erreur",
          description: "Impossible de supprimer la publication",
          variant: "destructive",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const togglePublishStatus = (pub) => {
    setIsLoading(true);
    axios
      .put(`${BackendUrl}/api/bannersRoutes/${pub._id}`, {
        pub: !pub.pub,
      }, getAdminAuthConfig())
      .then(() => {
        toast({
          title: "Succès",
          description: `Publication ${!pub.pub ? "activée" : "désactivée"}`,
          style: {
            backgroundColor: COLORS.paleGreen,
            borderColor: COLORS.green,
          },
        });
        fetchPublications();
      })
      .catch((error) => {
        toast({
          title: "Erreur",
          description: "Impossible de modifier le statut de la publication",
          variant: "destructive",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEdit = (pub) => {
    setEditItem(pub);
    setTitle(pub.title || "");
    setDescription(pub.description || "");
    setIsPublished(pub.pub || false);
    setPreviewImage(pub.image);
  };

  useEffect(() => {
    fetchPublications();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-orange-50 p-4 md:p-6">
      <h1
        className="text-2xl md:text-3xl font-bold mb-6 text-center"
        style={{ color: COLORS.darkGreen }}
      >
        Gestion des Bannières
      </h1>

      <Button
        onClick={fetchPublications}
        className="mb-6 flex items-center gap-2"
        style={{ backgroundColor: COLORS.green, color: COLORS.white }}
        disabled={isRefreshing}
      >
        <RefreshCw
          className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
        />
        {isRefreshing ? "Actualisation..." : "Actualiser"}
      </Button>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Publications existantes */}
        <Card
          className="border-0 shadow-lg overflow-hidden"
          style={{ backgroundColor: COLORS.white }}
        >
          <CardHeader
            className="pb-2"
            style={{ backgroundColor: COLORS.green, color: COLORS.white }}
          >
            <CardTitle className="flex items-center gap-2 text-xl">
              <Image className="h-5 w-5" />
              Publications Existantes
            </CardTitle>
            <CardDescription className="text-white opacity-80">
              {allPub.length} publication{allPub.length !== 1 ? "s" : ""}{" "}
              disponible{allPub.length !== 1 ? "s" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {isRefreshing ? (
              <div className="flex justify-center items-center h-48">
                <div
                  className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2"
                  style={{ borderColor: COLORS.orange }}
                ></div>
              </div>
            ) : allPub && allPub?.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                <AnimatePresence>
                  {Array.isArray(allPub) &&
                    allPub.map((pub, index) => (
                      <motion.div
                        key={pub._id}
                        className="relative group bg-white border rounded-lg shadow-sm p-3 hover:shadow-md transition-shadow"
                        style={{
                          borderColor: pub.pub ? COLORS.green : COLORS.gray,
                        }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <div className="flex flex-col md:flex-row gap-4">
                          <div
                            className="w-full md:w-1/3 h-48 rounded-lg overflow-hidden border-2 flex-shrink-0 cursor-pointer"
                            style={{
                              borderColor: pub.pub
                                ? COLORS.green
                                : COLORS.lightGray,
                              opacity: pub.pub ? 1 : 0.7,
                            }}
                            onClick={() => setShowFullImage(pub.image)}
                          >
                            <img
                              src={pub.image}
                              alt={pub.title || "Publication"}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-bold text-lg">
                                {pub.title || "Sans titre"}
                              </h3>
                              <div className="flex items-center">
                                <span
                                  className="text-sm mr-2"
                                  style={{
                                    color: pub.pub ? COLORS.green : COLORS.gray,
                                  }}
                                >
                                  {pub.pub ? "Publié" : "Non publié"}
                                </span>
                                <Switch
                                  checked={pub.pub}
                                  onCheckedChange={() =>
                                    togglePublishStatus(pub)
                                  }
                                  disabled={isLoading}
                                  className={`${
                                    pub.pub ? "bg-green-500" : "bg-gray-200"
                                  }`}
                                />
                              </div>
                            </div>

                            <p className="text-gray-700 mb-3 line-clamp-3">
                              {pub.description || "Aucune description"}
                            </p>

                            <div className="text-sm text-gray-500 mb-3">
                              Ajouté le:{" "}
                              {new Date(pub.date).toLocaleDateString("fr-FR")}
                            </div>

                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleEdit(pub)}
                                className="bg-blue-500 hover:bg-blue-600 text-white"
                              >
                                Modifier
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => setConfirmDelete(pub)}
                                className="bg-red-500 hover:bg-red-600 text-white"
                              >
                                Supprimer
                              </Button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                </AnimatePresence>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48">
                <AlertCircle
                  className="h-12 w-12 mb-4"
                  style={{ color: COLORS.gray }}
                />
                <p className="text-center text-gray-500">
                  Aucune publication enregistrée pour le moment
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ajouter/Modifier une publication */}
        <Card
          className="border-0 shadow-lg"
          style={{ backgroundColor: COLORS.white }}
        >
          <CardHeader
            className="pb-2"
            style={{ backgroundColor: COLORS.green, color: COLORS.white }}
          >
            <CardTitle className="flex items-center gap-2 text-xl">
              {editItem ? (
                <>
                  <FileText className="h-5 w-5" />
                  Modifier la Publication
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5" />
                  Ajouter une Publication
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={creer} className="space-y-4">
              <div className="space-y-4">
                {/* Titre */}
                <div className="space-y-2">
                  <Label htmlFor="title">Titre</Label>
                  <Input
                    id="title"
                    placeholder="Titre de la bannière"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Description de la bannière"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                  />
                </div>

                {/* État de publication */}
                <div className="flex items-center space-x-2">
                  <Switch
                    id="pub-status"
                    checked={isPublished}
                    onCheckedChange={setIsPublished}
                  />
                  <Label
                    htmlFor="pub-status"
                    className="flex items-center gap-2"
                  >
                    {isPublished ? (
                      <>
                        <Eye className="h-4 w-4" />
                        Publiée
                      </>
                    ) : (
                      <>
                        <EyeOff className="h-4 w-4" />
                        Non publiée
                      </>
                    )}
                  </Label>
                </div>

                {/* Sélection d'image */}
                <div
                  className="w-full h-64 border-2 border-dashed rounded-lg flex flex-col items-center justify-center p-4 relative"
                  style={{
                    borderColor: previewImage
                      ? COLORS.orange
                      : COLORS.lightGray,
                    backgroundColor: COLORS.paleGreen,
                  }}
                >
                  {previewImage ? (
                    <div className="relative w-full h-full">
                      <img
                        src={previewImage}
                        alt="Aperçu"
                        className="w-full h-full object-contain rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPreviewImage(null);
                          setImage(null);
                        }}
                        className="absolute top-2 right-2 bg-red-500 rounded-full p-2 text-white shadow-md"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center text-center">
                      <Camera size={48} style={{ color: COLORS.gray }} />
                      <p
                        className="mt-4 font-medium"
                        style={{ color: COLORS.gray }}
                      >
                        Glissez-déposez une image ici
                      </p>
                      <p
                        className="mt-2 text-sm"
                        style={{ color: COLORS.gray }}
                      >
                        ou cliquez pour sélectionner
                      </p>
                      <p
                        className="mt-2 text-xs"
                        style={{ color: COLORS.gray }}
                      >
                        Formats acceptés: JPEG, PNG, WEBP, GIF (max: 5MB)
                      </p>
                    </div>
                  )}
                  <Input
                    type="file"
                    id="publication-image"
                    onChange={handleImageChange}
                    className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                  />
                </div>

                <div className="flex gap-3">
                  {editItem && (
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={resetForm}
                    >
                      Annuler
                    </Button>
                  )}

                  <Button
                    type="submit"
                    className="flex-1 py-2 rounded-md flex items-center justify-center transition-all transform hover:scale-105 gap-2 text-white"
                    style={{ backgroundColor: COLORS.orange }}
                    disabled={isLoading || (!image && !editItem)}
                  >
                    {isLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        {editItem
                          ? "Mise à jour..."
                          : "Publication en cours..."}
                      </>
                    ) : (
                      <>
                        {editItem ? (
                          <>
                            <FileText className="h-4 w-4" />
                            Mettre à jour
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            Publier
                          </>
                        )}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Modal de confirmation de suppression */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      >
        <DialogContent
          className="sm:max-w-md"
          style={{ backgroundColor: COLORS.white }}
        >
          <DialogHeader>
            <DialogTitle className="text-red-600">
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              Êtes-vous sûr de vouloir supprimer cette publication ? Cette
              action ne peut pas être annulée.
            </p>
            {confirmDelete && (
              <div className="mt-4 space-y-2">
                <p>
                  <strong>Titre:</strong> {confirmDelete.title || "Sans titre"}
                </p>
                <div
                  className="p-2 border rounded"
                  style={{ borderColor: COLORS.lightGray }}
                >
                  <img
                    src={confirmDelete.image}
                    alt="Publication à supprimer"
                    className="w-full h-32 object-contain"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              className="mr-2"
              style={{ borderColor: COLORS.gray, color: COLORS.gray }}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button
              onClick={() => deletProductPub(confirmDelete?._id)}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white"
            >
              {isLoading ? "Suppression..." : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal pour afficher l'image en plein écran */}
      <Dialog
        open={!!showFullImage}
        onOpenChange={(open) => !open && setShowFullImage(null)}
      >
        <DialogContent
          className="sm:max-w-4xl h-auto max-h-screen overflow-hidden p-0"
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <div className="relative w-full h-full">
            <Button
              className="absolute top-2 right-2 bg-white rounded-full z-10"
              size="icon"
              onClick={() => setShowFullImage(null)}
            >
              <X className="h-4 w-4" />
            </Button>
            {showFullImage && (
              <img
                src={showFullImage}
                alt="Vue agrandie"
                className="w-full h-auto max-h-[80vh] object-contain bg-black"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProductPub;
